import { decodeJwt } from 'jose';

/**
 * Admin v2 "log in as user" (API-CONTRACT §5): the tab-scoped impersonation record, DI-free.
 *
 * The admin's own bearer stays in `localStorage` (`USER_SETTING_PREFIX-AUTH`, shared by every tab) and is NEVER
 * overwritten. The impersonation token lives in `sessionStorage` under `AUTH_IMP` -- this tab only -- so the other
 * tabs stay admin. While a record exists in this tab:
 *   - `AuthService.getToken()` returns ITS token (never the admin's, even once expired: the request is refused and the
 *     session ends instead -- an expired record must not silently turn the tab back into the admin);
 *   - nothing writes or removes `localStorage` AUTH, nothing calls `/auth/refresh` or step-up, nothing broadcasts on
 *     `xc-auth` and incoming broadcasts are ignored.
 *
 * Read by `AuthService`, `LightHttp`, `RefreshService`, `CrossTabAuthService`, `utils/edge-owner.ts` and the banner gate,
 * so it must stay tiny and free of the service graph (it ships in `_app`).
 */
export const IMPERSONATION_STORAGE_KEY = 'AUTH_IMP';

export interface ImpersonationTarget {
    userId: string;
    email: string;
    name?: string | null;
    packageType?: string | null;
}

export interface ImpersonationRecord {
    token: string;
    sessionId: string;
    /** ISO-8601 UTC (server's `expiresAt`). */
    expiresAt: string;
    target: ImpersonationTarget;
    /** Same-origin admin path to go back to when the session ends. */
    returnTo: string;
}

/** Where the tab goes when the record carries no usable `returnTo`. */
export const DEFAULT_IMPERSONATION_RETURN = '/admin/users';

function sessionStore(): Storage | null {
    try {
        return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
    } catch {
        return null; // storage blocked (privacy mode / sandboxed iframe)
    }
}

/** Same-origin path only (`/x`, never `//host`, `/\host` or an absolute URL) -- `returnTo` is never an open redirect. */
export function safeReturnTo(path: unknown): string {
    if (typeof path !== 'string' || path.length === 0 || path.length > 2048) return DEFAULT_IMPERSONATION_RETURN;
    if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return DEFAULT_IMPERSONATION_RETURN;
    return path;
}

function isRecord(v: any): v is ImpersonationRecord {
    return !!v && typeof v === 'object' &&
        typeof v.token === 'string' && v.token.length > 0 &&
        typeof v.sessionId === 'string' &&
        typeof v.expiresAt === 'string' &&
        !!v.target && typeof v.target === 'object' && typeof v.target.email === 'string';
}

// getToken() runs for every request: memoise the parse on the raw string.
let memoRaw: string | null = null;
let memoRecord: ImpersonationRecord | null = null;

/** The record in this tab, expired or not; null when absent or malformed (a malformed value is removed). */
export function readImpersonation(storage: Pick<Storage, 'getItem' | 'removeItem'> | null = sessionStore()): ImpersonationRecord | null {
    if (!storage) return null;
    let raw: string | null = null;
    try {
        raw = storage.getItem(IMPERSONATION_STORAGE_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;
    if (raw === memoRaw) return memoRecord;
    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch {
        parsed = null;
    }
    if (!isRecord(parsed)) {
        try { storage.removeItem(IMPERSONATION_STORAGE_KEY); } catch { /* ignore */ }
        return null;
    }
    parsed.returnTo = safeReturnTo(parsed.returnTo);
    memoRaw = raw;
    memoRecord = parsed;
    return parsed;
}

/** True while this tab holds an impersonation record (expired or not): the "impersonation mode" every guard reads. */
export function hasImpersonationRecord(): boolean {
    return readImpersonation() !== null;
}

/** Epoch ms of the record's expiry; NaN-safe (an unparseable date counts as already expired). */
export function impersonationExpiresAtMs(rec: Pick<ImpersonationRecord, 'expiresAt'>): number {
    const t = Date.parse(rec.expiresAt);
    return Number.isFinite(t) ? t : 0;
}

export function isImpersonationExpired(rec: Pick<ImpersonationRecord, 'expiresAt'>, now: number = Date.now()): boolean {
    return impersonationExpiresAtMs(rec) <= now;
}

/** The record when present AND unexpired. */
export function activeImpersonation(now: number = Date.now()): ImpersonationRecord | null {
    const rec = readImpersonation();
    return rec && !isImpersonationExpired(rec, now) ? rec : null;
}

/** The `imp` claim (impersonation session id) of a JWT, or null. Never throws. */
export function tokenImpersonationSessionId(token: string | null | undefined): string | null {
    if (!token) return null;
    try {
        const imp = (decodeJwt(token) as { imp?: unknown }).imp;
        return typeof imp === 'string' && imp.length > 0 ? imp : null;
    } catch {
        return null;
    }
}

/**
 * Impersonation mode for request-level guards: a record in this tab, OR the bearer about to be sent is itself an
 * impersonation token (copied somewhere it should not be -- contract §4 user-details `impersonation`). Either way no
 * refresh, no step-up, a 401 ends the session.
 */
export function isImpersonating(token?: string | null): boolean {
    return hasImpersonationRecord() || tokenImpersonationSessionId(token) !== null;
}

type Listener = (rec: ImpersonationRecord | null) => void;
const listeners = new Set<Listener>();

/** Change notifications for this tab (write/clear). Returns the unsubscribe. */
export function onImpersonationChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

function emit(rec: ImpersonationRecord | null): void {
    listeners.forEach((l) => {
        try { l(rec); } catch { /* a listener never breaks the auth path */ }
    });
}

export function writeImpersonation(rec: ImpersonationRecord): void {
    const storage = sessionStore();
    if (!storage) throw new Error('This browser blocks session storage; viewing as a user needs it.');
    const value: ImpersonationRecord = { ...rec, returnTo: safeReturnTo(rec.returnTo) };
    storage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(value));
    emit(readImpersonation(storage));
}

export function clearImpersonation(): void {
    const storage = sessionStore();
    try { storage?.removeItem(IMPERSONATION_STORAGE_KEY); } catch { /* ignore */ }
    memoRaw = null;
    memoRecord = null;
    emit(null);
}

/** Why a session ends locally (for the notice and the end request's `reason`). */
export type ImpersonationEndReason = 'user' | 'logout' | 'expired' | 'ended' | 'unauthorized' | 'session_cleared';

/** One-shot note for the page the tab lands on after a session ended on its own (expiry, server end). */
export const IMPERSONATION_ENDED_NOTICE_KEY = 'AUTH_IMP_ENDED';

export function writeImpersonationEndedNotice(reason: ImpersonationEndReason): void {
    try { sessionStore()?.setItem(IMPERSONATION_ENDED_NOTICE_KEY, reason); } catch { /* ignore */ }
}

/** Reads AND removes the note. */
export function takeImpersonationEndedNotice(): ImpersonationEndReason | null {
    const storage = sessionStore();
    if (!storage) return null;
    try {
        const v = storage.getItem(IMPERSONATION_ENDED_NOTICE_KEY);
        if (v) storage.removeItem(IMPERSONATION_ENDED_NOTICE_KEY);
        return (v as ImpersonationEndReason) || null;
    } catch {
        return null;
    }
}

/**
 * Seams for the low-level modules (`LightHttp`, `LoginService`, `AuthService`) that cannot import the impersonation
 * service (it imports them). `ImpersonationService` installs the real handlers when constructed; the fallbacks keep
 * the guarantees with no service at all: the record is cleared and the tab goes back to `returnTo`.
 */
export interface ImpersonationHandlers {
    end: (reason: ImpersonationEndReason) => void;
    forbidden: () => void;
}

const fallbackHandlers: ImpersonationHandlers = {
    end: (reason) => {
        const rec = readImpersonation();
        if (!rec) return;
        clearImpersonation();
        if (reason !== 'user' && reason !== 'logout') writeImpersonationEndedNotice(reason);
        if (typeof window !== 'undefined') {
            try { window.location.assign(rec?.returnTo ?? DEFAULT_IMPERSONATION_RETURN); } catch { /* jsdom */ }
        }
    },
    forbidden: () => undefined,
};

let handlers: ImpersonationHandlers = fallbackHandlers;

export function setImpersonationHandlers(h: ImpersonationHandlers | null): void {
    handlers = h ?? fallbackHandlers;
}

export function requestImpersonationEnd(reason: ImpersonationEndReason): void {
    handlers.end(reason);
}

export function notifyImpersonationForbidden(): void {
    handlers.forbidden();
}

/** Contract §4 `impersonation` on `/auth/user-details`, when the token carries `imp`. */
export interface ServerImpersonationInfo {
    sessionId: string;
    actorEmail?: string | null;
    expiresAt?: string | null;
}

/** Error a request fails with when it was refused locally because the impersonation session is over. */
export class ImpersonationEndedError extends Error {
    status = 401;
    constructor(message = 'Your "view as user" session has ended.') {
        super(message);
        this.name = 'Session ended';
    }
}

/** User-facing words for a 403 `impersonation_forbidden`. */
export const IMPERSONATION_FORBIDDEN_MESSAGE = 'Not allowed while viewing as a user.';
